import axios from "axios";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { API_BASE_URL } from "../config";
import { useAuth } from '../components/shared/AuthContext';
import {
  Box,
  Container,
  Typography,
  TextField,
  Autocomplete,
  Button,
  Card,
  CardContent,
  Grid,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Chip,
  Stack,
  Divider,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  FormHelperText,
  Switch,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import SearchIcon from "@mui/icons-material/Search";
import PrintIcon from "@mui/icons-material/Print";
import DownloadIcon from "@mui/icons-material/Download";
import CloseIcon from "@mui/icons-material/Close";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ReceiptIcon from "@mui/icons-material/Receipt";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { getCustomerOptions } from '../utils/utils';
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
// import "../css/FixedDeposit.css";

// Styled components
const StyledModal = styled(Dialog)(({ theme }) => ({
  "& .MuiDialog-paper": {
    maxWidth: "90vw",
    maxHeight: "90vh",
    padding: theme.spacing(4),
  },
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontWeight: "bold",
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.common.white,
}));

const StyledChip = styled(Chip)(({ theme, status }) => ({
  fontWeight: "bold",
  backgroundColor: status === "active" ? theme.palette.success.main : theme.palette.error.main,
  color: theme.palette.common.white,
}));

const StatsCard = styled(Card)(({ theme }) => ({
  height: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  padding: theme.spacing(2),
  transition: "transform 0.2s",
  "&:hover": {
    transform: "translateY(-4px)",
  },
}));

const FixedDeposit = () => {
  const { permissions, roleName, data } = useAuth();
  const branchId = data?.branchId || null;
  const navigate = useNavigate();
  const location = useLocation();
  const [customerUniqueId, setCustomerUniqueId] = useState(location.state?.customerUniqueId || null);
  const [customerDetails, setCustomerDetails] = useState(null);
  const [error, setError] = useState("");
  const [customerOptions, setCustomerOptions] = useState([]);
  const [personalDetails, setPersonalDetails] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [dataFetchError, setDataFetchError] = useState("");
  const [fixedDepositList, setFixedDepositList] = useState([]);
  const [filteredDeposits, setFilteredDeposits] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [showAddModal, setShowAddModal] = useState(false);
  const [showReceiptModal, setShowReceiptModal] = useState(false);
  const [showAlertModal, setShowAlertModal] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [selectedDeposit, setSelectedDeposit] = useState(null);
  const [formErrors, setFormErrors] = useState({});

  // Fixed deposit form state
  const [depositForm, setDepositForm] = useState({
    CIFNumber: "",
    AccountNumber: "",
    PANNumber: "",
    SrNo: "",
    amount: "",
    interestRate: "15.0", // TODO: Fetch dynamically from API
    deposit_date: new Date().toISOString().split("T")[0],
    maturity_date: "",
    periods: "3",
    nominee: "No",
    nomineeName: "",
    repayable_to: "",
    totalInterest: "0.00",
    maturity_amount: "0.00",
  });

  // Role-based permissions
  const isDataEntryOperator = roleName === "Data Entry Operator";
  const isAdmin = roleName === "admin";
  const isSuperAdmin = roleName === "superadmin";
  const canViewAllBranches = isSuperAdmin;
  const hasViewPermission = isDataEntryOperator || isAdmin || permissions?.some((p) => p.page === "fixedDeposit" && p.view);
  const hasAddPermission = permissions?.some((p) => p.page === "fixedDeposit" && p.add) || isAdmin || isSuperAdmin;

  // Generate unique identifiers
  const generateSerialNumber = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");
    const random = Math.floor(Math.random() * 10000).toString().padStart(4, "0");
    return `${day}${month}${year}${hours}${minutes}${seconds}${random}`;
  }, []);

  const generateCIFNumber = useCallback(() => {
    const random = Math.floor(Math.random() * 10000000).toString().padStart(8, "0");
    return `CIF${random}`;
  }, []);

  const generateAccountNumber = useCallback(() => {
    return `FD${Date.now().toString().slice(-8)}`;
  }, []);

  // Convert number to words
  const convertToWords = useCallback((num) => {
    const single = ["", "One", "Two", "Three", "Four", "Five", "Six", "Seven", "Eight", "Nine"];
    const double = ["Ten", "Eleven", "Twelve", "Thirteen", "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"];
    const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
    const formatTens = (n) => {
      if (n < 10) return single[n];
      if (n < 20) return double[n - 10];
      return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + single[n % 10] : "");
    };
    const formatHundreds = (n) => {
      if (n < 100) return formatTens(n);
      return single[Math.floor(n / 100)] + " Hundred" + (n % 100 !== 0 ? " " + formatTens(n % 100) : "");
    };

    if (num === 0) return "Zero";
    num = Math.floor(num); // Handle integers only
    const thousand = Math.floor(num / 1000) % 100;
    const lakh = Math.floor(num / 100000) % 100;
    const crore = Math.floor(num / 10000000) % 100;

    let words = "";
    if (crore > 0) words += formatTens(crore) + " Crore ";
    if (lakh > 0) words += formatTens(lakh) + " Lakh ";
    if (thousand > 0) words += formatTens(thousand) + " Thousand ";
    if (num % 1000 > 0) words += formatHundreds(num % 1000);
    return words.trim() + " Only";
  }, []);

  // Calculate maturity date and amount
  const calculateMaturity = useCallback(() => {
    if (!depositForm.deposit_date || !depositForm.periods || !depositForm.amount || !depositForm.interestRate) {
      return;
    }

    try {
      const depositDate = new Date(depositForm.deposit_date);
      const maturityDate = new Date(depositDate);
      maturityDate.setMonth(maturityDate.getMonth() + parseInt(depositForm.periods));
      const formattedMaturityDate = maturityDate.toISOString().split("T")[0];

      const principal = parseFloat(depositForm.amount);
      const rate = parseFloat(depositForm.interestRate) / 100;
      const timeInYears = parseInt(depositForm.periods) / 12;
      const interestAmount = principal * rate * timeInYears;
      const maturityAmount = principal + interestAmount;

      setDepositForm((prev) => ({
        ...prev,
        maturity_date: formattedMaturityDate,
        totalInterest: interestAmount.toFixed(2),
        maturity_amount: maturityAmount.toFixed(2),
        CIFNumber: prev.CIFNumber || generateCIFNumber(),
        SrNo: prev.SrNo || generateSerialNumber(),
        AccountNumber: prev.AccountNumber || generateAccountNumber(),
      }));
      setFormErrors((prev) => ({ ...prev, amount: "", interestRate: "", periods: "" }));
    } catch (error) {
      setError(`Calculation error: ${error.message}`);
    }
  }, [depositForm.deposit_date, depositForm.periods, depositForm.amount, depositForm.interestRate, generateCIFNumber, generateSerialNumber, generateAccountNumber]);

  // Fetch personal details
  const fetchPersonalDetails = async () => {
    setIsLoading(true);
    setDataFetchError("");
    try {
      let url = `${API_BASE_URL}/api/getModelData/personal_details?fields=id,customerUniqueId,name`;
      if (!canViewAllBranches && branchId) {
        url += `&filter={"branchId":${branchId}}`;
      } else if (!canViewAllBranches && !branchId) {
        setDataFetchError("Branch ID not available.");
        setPersonalDetails([]);
        setIsLoading(false);
        return;
      }

      const response = await axios.get(url, { timeout: 10000 });
      const customerData = Array.isArray(response.data.data) ? response.data.data : Array.isArray(response.data) ? response.data : [];
      setPersonalDetails(customerData);
      if (customerData.length === 0 && !canViewAllBranches) {
        setDataFetchError("No customers found for your branch.");
      }
    } catch (error) {
      setDataFetchError(`Error fetching personal details: ${error.message}`);
      setPersonalDetails([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch branches
  const fetchBranches = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/branch_details`, { timeout: 10000 });
      const branchData = response.data.data || [];
      setBranches(branchData);
      if (!canViewAllBranches && branchId) {
        const userBranch = branchData.find((b) => String(b.id) === String(branchId));
        if (userBranch) {
          setSelectedBranch(userBranch);
        } else {
          setDataFetchError("User branch not found.");
        }
      }
    } catch (error) {
      setDataFetchError(`Error fetching branches: ${error.message}`);
    }
  };

  // Fetch customer options
  const fetchCustomerOptions = async () => {
    setIsLoading(true);
    setDataFetchError("");
    try {
      const branchFilter = canViewAllBranches ? null : branchId;
      const options = await getCustomerOptions(branchFilter);
      const enhancedOptions = options.map((option) => {
        const personalDetail = personalDetails.find((detail) => detail.id === option.value);
        return {
          ...option,
          name: personalDetail?.name || option.label,
          customerUniqueId: personalDetail?.customerUniqueId || option.label,
        };
      });
      setCustomerOptions(enhancedOptions);
      if (enhancedOptions.length === 0) {
        setDataFetchError("No customers found for your branch.");
      }
    } catch (error) {
      setDataFetchError(`Error fetching customer data: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch fixed deposits
  const fetchFixedDeposits = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API_BASE_URL}/api/getModelData/fixeddeposite`, { timeout: 10000 });
      let deposits = response.data.data || [];
      if (!canViewAllBranches && branchId) {
        deposits = deposits.filter((deposit) => String(deposit.branchId) === String(branchId));
      }
      deposits = deposits.map((deposit) => ({
        ...deposit,
        customerName: deposit.customerName ?? "Unknown",
        accountNumber: deposit.AccountNumber ?? `FD${deposit.id}`,
        interestRate: deposit.interest ?? "0.00",
        period: deposit.periods ?? "0",
        depositDate: deposit.deposit_date ?? null,
        maturityDate: deposit.maturity_date ?? null,
        maturityAmount: deposit.maturity_amount ?? "0.00",
        status: deposit.active ? "active" : "inactive",
        srNo: deposit.SrNo ?? "N/A",
        cifNumber: deposit.CIFNumber ?? "N/A",
        panNumber: deposit.PANNumber ?? "N/A",
      }));
      setFixedDepositList(deposits);
      setFilteredDeposits(deposits);
    } catch (error) {
      setDataFetchError(`Error fetching fixed deposits: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle status toggle
  const handleStatusToggle = async (depositId, currentStatus) => {
    setIsLoading(true);
    try {
      const payload = {
        model: "fixeddeposite",
        type: "Edit",
        id: depositId,
        active: !currentStatus,
      };
      const response = await axios.post(`${API_BASE_URL}/api/addDetails`, payload, { timeout: 10000 });
      if (response.status === 200 || response.status === 201) {
        setAlertMessage(`Fixed Deposit status updated to ${!currentStatus ? "Active" : "Inactive"}!`);
        setShowAlertModal(true);
        setFixedDepositList((prev) =>
          prev.map((deposit) =>
            deposit.id === depositId
              ? { ...deposit, active: !currentStatus, status: !currentStatus ? "active" : "inactive" }
              : deposit
          )
        );
        setFilteredDeposits((prev) =>
          prev.map((deposit) =>
            deposit.id === depositId
              ? { ...deposit, active: !currentStatus, status: !currentStatus ? "active" : "inactive" }
              : deposit
          )
        );
      }
    } catch (error) {
      setAlertMessage(`Failed to update status: ${error.message}`);
      setShowAlertModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // Data fetching effect
  useEffect(() => {
    if (!hasViewPermission) {
      setDataFetchError("You do not have permission to view fixed deposits.");
      return;
    }
    fetchPersonalDetails();
    fetchBranches();
    fetchFixedDeposits();
  }, [branchId, hasViewPermission]);

  useEffect(() => {
    if (personalDetails.length > 0 && (canViewAllBranches || branchId)) {
      fetchCustomerOptions();
    }
  }, [personalDetails, branchId, canViewAllBranches]);

  // Calculate maturity on form changes
  useEffect(() => {
    calculateMaturity();
  }, [calculateMaturity]);

  // Handle form input changes
  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setDepositForm((prev) => ({ ...prev, [name]: value }));
    setFormErrors((prev) => ({ ...prev, [name]: "" }));
  };

  // Validate form
  const validateForm = () => {
    const errors = {};
    const amount = parseFloat(depositForm.amount);
    const interestRate = parseFloat(depositForm.interestRate);
    const periods = parseInt(depositForm.periods);

    if (!amount || amount <= 0) {
      errors.amount = "Amount must be greater than zero.";
    } else if (amount < 1000) {
      errors.amount = "Minimum deposit amount is ₹1000.";
    }

    if (!interestRate || interestRate <= 0) {
      errors.interestRate = "Interest rate must be greater than zero.";
    } else if (interestRate > 20) {
      errors.interestRate = "Interest rate cannot exceed 20%.";
    }

    if (!periods || periods <= 0) {
      errors.periods = "Period must be a valid number of months.";
    }

    if (depositForm.nominee === "Yes" && !depositForm.nomineeName.trim()) {
      errors.nomineeName = "Nominee name is required.";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Open add fixed deposit modal
  const handleAddClick = () => {
    if (!customerUniqueId || !customerDetails) {
      setError("Please select a customer");
      return;
    }
    setError("");
    setDepositForm((prev) => ({
      ...prev,
      AccountNumber: generateAccountNumber(),
      CIFNumber: generateCIFNumber(),
      SrNo: generateSerialNumber(),
    }));
    setShowAddModal(true);
  };

  // Submit fixed deposit form
  const handleDepositSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsLoading(true);
    try {
      // First, check if an account with type "02" already exists for this customer
      const accountResponse = await axios.get(`${API_BASE_URL}/api/getModelData/account_details`);
      console.log('Fetched accounts:', accountResponse.data);

      let existingAccounts = [];
      if (accountResponse.data && accountResponse.data.data && Array.isArray(accountResponse.data.data)) {
        existingAccounts = accountResponse.data.data;
      } else if (Array.isArray(accountResponse.data)) {
        existingAccounts = accountResponse.data;
      } else if (accountResponse.data && typeof accountResponse.data === 'object') {
        existingAccounts = [accountResponse.data];
      }

      console.log('Checking for existing account for customer:', customerUniqueId);
      const existingAccount = existingAccounts.find(
        account => account.customerUniqueId === customerUniqueId && account.accountType === '02'
      );
      console.log('Existing account found:', existingAccount);

      // If no account exists, create one
      if (!existingAccount) {
        console.log('No existing account found, creating new account');
        const accountNumber = `FD${customerUniqueId}${Date.now()}`;
        const accountPayload = {
          model: 'account_details',
          type: 'Add',
          customerUniqueId: customerUniqueId,
          accountNumber: accountNumber,
          accountType: '02',
          branchId: canViewAllBranches && selectedBranch ? selectedBranch.id : branchId,
          active: true,
          balance: depositForm.amount ? String(parseFloat(depositForm.amount)) : "0",
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString()
        };

        console.log('Creating account with payload:', accountPayload);
        try {
          const accountCreateResponse = await axios.post(`${API_BASE_URL}/api/addDetails`, accountPayload);
          console.log('Account creation response:', accountCreateResponse.data);

          if (!accountCreateResponse.data) {
            throw new Error('Failed to create account - no response data');
          }
        } catch (accountError) {
          console.error('Error creating account:', accountError);
          throw new Error(`Failed to create account: ${accountError.message}`);
        }
      }

      // Then create the fixed deposit
      const payload = {
        model: "fixeddeposite",
        type: "Add",
        customerUniqueId,
        customerName: customerDetails?.name ?? "",
        branchId: canViewAllBranches && selectedBranch ? selectedBranch.id : branchId,
        amount: String(parseFloat(depositForm.amount)),
        monthlydue: "",
        nominee_name: depositForm.nominee === "Yes" ? depositForm.nomineeName : "",
        repayable_to: depositForm.repayable_to ?? "",
        periods: String(parseInt(depositForm.periods)),
        deposit_date: depositForm.deposit_date,
        maturity_date: depositForm.maturity_date,
        maturity_amount: String(parseFloat(depositForm.maturity_amount)),
        interest: String(parseFloat(depositForm.interestRate)),
        active: true,
        SrNo: depositForm.SrNo,
        CIFNumber: depositForm.CIFNumber,
        AccountNumber: depositForm.AccountNumber,
        PANNumber: depositForm.PANNumber,
      };

      console.log('Creating fixed deposit with payload:', payload);
      const response = await axios.post(`${API_BASE_URL}/api/addDetails`, payload);
      if (response.status === 200 || response.status === 201) {
        setAlertMessage("Fixed Deposit added successfully!");
        setShowAlertModal(true);
        setShowAddModal(false);
        setDepositForm({
          CIFNumber: "",
          AccountNumber: "",
          PANNumber: "",
          SrNo: "",
          amount: "",
          interestRate: "15.0",
          deposit_date: new Date().toISOString().split("T")[0],
          maturity_date: "",
          periods: "3",
          nominee: "No",
          nomineeName: "",
          repayable_to: "",
          totalInterest: "0.00",
          maturity_amount: "0.00",
        });
        setCustomerUniqueId(null);
        setCustomerDetails(null);
        fetchFixedDeposits();
      }
    } catch (error) {
      setAlertMessage(`Failed to add fixed deposit: ${error.message}`);
      setShowAlertModal(true);
    } finally {
      setIsLoading(false);
    }
  };

  // View fixed deposit details
  const handleViewDetails = (deposit) => {
    setSelectedDeposit({
      ...deposit,
      customerName: deposit.customerName ?? "Unknown",
      accountNumber: deposit.AccountNumber ?? `FD${deposit.id}`,
      interestRate: deposit.interest ?? "0.00",
      period: deposit.periods ?? "0",
      depositDate: deposit.deposit_date ?? null,
      maturityDate: deposit.maturity_date ?? null,
      maturityAmount: deposit.maturity_amount ?? "0.00",
      status: deposit.active ? "active" : "inactive",
      srNo: deposit.SrNo ?? "N/A",
      cifNumber: deposit.CIFNumber ?? "N/A",
      panNumber: deposit.PANNumber ?? "N/A",
      nominee_name: deposit.nominee_name ?? "N/A",
    });
    setShowReceiptModal(true);
  };

  // View receipt
  const handleViewReceipt = (deposit) => {
    setSelectedDeposit({
      ...deposit,
      customerName: deposit.customerName ?? "Unknown",
      accountNumber: deposit.AccountNumber ?? `FD${deposit.id}`,
      interestRate: deposit.interest ?? "0.00",
      period: deposit.periods ?? "0",
      depositDate: deposit.deposit_date ?? null,
      maturityDate: deposit.maturity_date ?? null,
      maturityAmount: deposit.maturity_amount ?? "0.00",
      status: deposit.active ? "active" : "inactive",
      srNo: deposit.SrNo ?? "N/A",
      cifNumber: deposit.CIFNumber ?? "N/A",
      panNumber: deposit.PANNumber ?? "N/A",
      nominee_name: deposit.nominee_name ?? "N/A",
    });
    setShowReceiptModal(true);
  };

  // Download receipt as PDF
  const handleDownloadReceipt = async () => {
    if (!selectedDeposit) return;
    const receiptContent = document.getElementById("fixed-deposit-receipt");
    if (!receiptContent) return;

    try {
      const canvas = await html2canvas(receiptContent, { scale: 2 });
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF();
      pdf.addImage(imgData, "PNG", 10, 10, 190, 0);
      pdf.save(`fixed_deposit_receipt_${selectedDeposit.accountNumber}.pdf`);
      setAlertMessage("Receipt downloaded successfully!");
      setShowAlertModal(true);
      setShowReceiptModal(false);
    } catch (error) {
      setAlertMessage(`Error downloading receipt: ${error.message}`);
      setShowAlertModal(true);
    }
  };

  // Print receipt
  const handlePrintReceipt = () => {
    const receiptContent = document.getElementById("fixed-deposit-receipt");
    if (!receiptContent) return;

    const printWindow = window.open("", "_blank");
    printWindow.document.write("<html><head><title>Fixed Deposit Receipt</title>");
    printWindow.document.write("<style>");
    printWindow.document.write(`
      body { font-family: Arial, sans-serif; }
      .fixed-deposit-receipt { border: 1px solid #000; padding: 15px; }
      .receipt-header { border-bottom: 1px solid #000; padding-bottom: 10px; margin-bottom: 15px; text-align: center; }
      .receipt-title { border: 1px solid #000; padding: 5px; margin: 10px auto; max-width: 300px; font-weight: bold; }
      .receipt-row { display: flex; margin-bottom: 10px; }
      .receipt-field { flex: 1; padding: 0 5px; }
      .field-label { font-weight: bold; margin-right: 5px; }
      .field-value { border-bottom: 1px dotted #999; padding-bottom: 2px; min-height: 20px; }
      .receipt-field.full-width { flex: 0 0 100%; max-width: 100%; }
      .deposit-details-table table { width: 100%; border-collapse: collapse; }
      table { width: 100%; border-collapse: collapse; }
      .deposit-details-table th, .deposit-details-table td { border: 1px solid #000; padding: 8px; text-align: center; }
      .not-transferable { font-weight: bold; margin: 10px 0; font-style: italic; }
      .signature-area { display: flex; justify-content: space-between; margin-top: 30px; }
      .signature-area > div { flex: 1; padding: 10px; min-height: 50px; }
      .text-center { text-align: center; }
    `);
    printWindow.document.write("</style></head><body>");
    printWindow.document.write(receiptContent.innerHTML);
    printWindow.document.write("</body></html>");
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.focus();
      printWindow.print();
    };
  };

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      const filtered = fixedDepositList.filter(
        (deposit) =>
          deposit.accountNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
          deposit.customerName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredDeposits(filtered);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm, fixedDepositList]);

  // Memoized stats
  const stats = useMemo(
    () => ({
      total: fixedDepositList.length,
      active: fixedDepositList.filter((d) => d.status === "active").length,
      closed: fixedDepositList.filter((d) => d.status !== "active").length,
    }),
    [fixedDepositList]
  );

  if (!hasViewPermission) {
    return (
      <Container maxWidth="xl">
        <Box sx={{ py: 4 }}>
          <Alert severity="error">You do not have permission to view this page.</Alert>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ py: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4 }}>
          Fixed Deposit Management
        </Typography>

        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {dataFetchError && (
          <Alert
            severity="error"
            action={
              <Button
                color="inherit"
                size="small"
                onClick={() => {
                  setDataFetchError("");
                  fetchPersonalDetails();
                  fetchBranches();
                  fetchFixedDeposits();
                }}
              >
                Retry
              </Button>
            }
            sx={{ mb: 4 }}
          >
            {dataFetchError}
          </Alert>
        )}

        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={4}>
            <StatsCard>
              <Box sx={{ mb: 2 }}>
                <AccountBalanceIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" color="primary.main" gutterBottom>
                {isLoading ? "..." : stats.total}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total Fixed Deposits
              </Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StatsCard>
              <Box sx={{ mb: 2 }}>
                <AccountBalanceIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" color="success.main" gutterBottom>
                {isLoading ? "..." : stats.active}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Active Fixed Deposits
              </Typography>
            </StatsCard>
          </Grid>
          <Grid item xs={12} md={4}>
            <StatsCard>
              <Box sx={{ mb: 2 }}>
                <AccountBalanceIcon color="error" sx={{ fontSize: 40 }} />
              </Box>
              <Typography variant="h3" color="error.main" gutterBottom>
                {isLoading ? "..." : stats.closed}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Closed Fixed Deposits
              </Typography>
            </StatsCard>
          </Grid>
        </Grid>

        <Paper elevation={3} sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>
            Select a Customer
          </Typography>

          {!isLoading && !dataFetchError && hasAddPermission && (
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} md={6}>
                <Autocomplete
                  options={customerOptions}
                  getOptionLabel={(option) => `${option.name} (${option.label})`}
                  value={customerDetails || null}
                  onChange={(event, value) => {
                    setCustomerUniqueId(value ? value.customerUniqueId : null);
                    setCustomerDetails(value || null);
                    setError("");
                  }}
                  filterOptions={(options, { inputValue }) => {
                    const lowerInput = inputValue.toLowerCase();
                    return options.filter(
                      (option) =>
                        option.name.toLowerCase().includes(lowerInput) ||
                        option.label.toLowerCase().includes(lowerInput)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={
                        canViewAllBranches
                          ? "Search Customer by Name or ID"
                          : `Search Customer by Name or ID (Branch: ${selectedBranch?.branchName || branchId || "Your Branch"})`
                      }
                      variant="outlined"
                      error={!!error}
                      helperText={error || (canViewAllBranches ? "Showing all customers" : "")}
                      InputProps={{
                        ...params.InputProps,
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon color="action" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  )}
                  isOptionEqualToValue={(option, value) => option.customerUniqueId === value.customerUniqueId}
                  loading={isLoading}
                  noOptionsText={isLoading ? "Loading..." : "No customers found."}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="contained"
                    onClick={handleAddClick}
                    disabled={!customerUniqueId || !customerDetails || isLoading}
                    startIcon={<AccountBalanceIcon />}
                  >
                    Add Fixed Deposit
                  </Button>
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setIsLoading(true);
                      fetchCustomerOptions().finally(() => setIsLoading(false));
                    }}
                    disabled={isLoading}
                    startIcon={<SearchIcon />}
                  >
                    Refresh Data
                  </Button>
                </Stack>
              </Grid>
            </Grid>
          )}
        </Paper>

        <Paper elevation={3} sx={{ p: 3 }}>
          <Typography variant="h6" gutterBottom>
            Fixed Deposit Accounts
          </Typography>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 3 }}>
            <TextField
              placeholder="Search by account number or name"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {filteredDeposits.length > 0 ? (
            <TableContainer component={Paper} elevation={0}>
              <Table>
                <TableHead>
                  <TableRow>
                    <StyledTableCell>Account No.</StyledTableCell>
                    <StyledTableCell>Customer Name</StyledTableCell>
                    <StyledTableCell>Customer ID</StyledTableCell>
                    <StyledTableCell align="right">Amount (₹)</StyledTableCell>
                    <StyledTableCell align="center">Interest Rate (%)</StyledTableCell>
                    <StyledTableCell>Deposit Date</StyledTableCell>
                    <StyledTableCell>Maturity Date</StyledTableCell>
                    <StyledTableCell>Period (Months)</StyledTableCell>
                    <StyledTableCell>Status</StyledTableCell>
                    <StyledTableCell align="center">Actions</StyledTableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredDeposits.map((deposit) => (
                    <TableRow key={deposit.id} hover>
                      <TableCell>{deposit.accountNumber}</TableCell>
                      <TableCell>{deposit.customerName}</TableCell>
                      <TableCell>{deposit.customerUniqueId}</TableCell>
                      <TableCell align="right">{deposit.amount?.toLocaleString("en-IN") ?? "0.00"}</TableCell>
                      <TableCell align="center">{deposit.interestRate}</TableCell>
                      <TableCell>
                        {deposit.depositDate
                          ? new Date(deposit.depositDate).toLocaleDateString("en-GB")
                          : "N/A"}
                      </TableCell>
                      <TableCell>
                        {deposit.maturityDate
                          ? new Date(deposit.maturityDate).toLocaleDateString("en-GB")
                          : "N/A"}
                      </TableCell>
                      <TableCell>{deposit.period}</TableCell>
                      <TableCell>
                        <StyledChip label={deposit.status} status={deposit.status} size="small" />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1} justifyContent="center">
                          <Tooltip title={deposit.active ? "Deactivate" : "Activate"}>
                            <Switch
                              checked={deposit.active}
                              onChange={() => handleStatusToggle(deposit.id, deposit.active)}
                              disabled={isLoading || !(isAdmin || isSuperAdmin)}
                            />
                          </Tooltip>
                          <Tooltip title="View Receipt">
                            <IconButton size="small" color="info" onClick={() => handleViewReceipt(deposit)}>
                              <ReceiptIcon />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="View Details">
                            <IconButton size="small" color="primary" onClick={() => handleViewDetails(deposit)}>
                              <VisibilityIcon />
                            </IconButton>
                          </Tooltip>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Alert severity="info">{isLoading ? "Loading fixed deposits..." : "No fixed deposit accounts found."}</Alert>
          )}
        </Paper>

        <StyledModal open={showAddModal} onClose={() => setShowAddModal(false)}>
          <DialogTitle>Create Fixed Deposit Account for {customerDetails?.name || "Customer"}</DialogTitle>
          <DialogContent dividers>
            <Box component="form" onSubmit={handleDepositSubmit} sx={{ mt: 2 }}>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="CIF Number"
                    name="CIFNumber"
                    value={depositForm.CIFNumber}
                    InputProps={{ readOnly: true }}
                    placeholder="Auto-generated"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Account Number"
                    name="AccountNumber"
                    value={depositForm.AccountNumber}
                    InputProps={{ readOnly: true }}
                    placeholder="Auto-generated"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="PAN Number"
                    name="PANNumber"
                    value={depositForm.PANNumber}
                    onChange={handleFormChange}
                    placeholder="Optional"
                    error={!!formErrors.PANNumber}
                    helperText={formErrors.PANNumber}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Sr.No"
                    name="SrNo"
                    value={depositForm.SrNo}
                    InputProps={{ readOnly: true }}
                    placeholder="Auto-generated"
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Deposit Amount (₹)"
                    name="amount"
                    value={depositForm.amount}
                    onChange={handleFormChange}
                    required
                    inputProps={{ min: "1000", step: "1000" }}
                    placeholder="e.g., 50000"
                    error={!!formErrors.amount}
                    helperText={formErrors.amount}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Total Interest"
                    name="totalInterest"
                    value={depositForm.totalInterest}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!formErrors.nominee}>
                    <InputLabel>Nominee</InputLabel>
                    <Select name="nominee" value={depositForm.nominee} onChange={handleFormChange} label="Nominee">
                      <MenuItem value="No">No</MenuItem>
                      <MenuItem value="Yes">Yes</MenuItem>
                    </Select>
                    {formErrors.nominee && <FormHelperText>{formErrors.nominee}</FormHelperText>}
                  </FormControl>
                </Grid>
                {depositForm.nominee === "Yes" && (
                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Nominee Name"
                      name="nomineeName"
                      value={depositForm.nomineeName}
                      onChange={handleFormChange}
                      required
                      error={!!formErrors.nomineeName}
                      helperText={formErrors.nomineeName}
                    />
                  </Grid>
                )}
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Repayable To"
                    name="repayable_to"
                    value={depositForm.repayable_to}
                    onChange={handleFormChange}
                  />
                </Grid>

                <Grid item xs={12}>
                  <Divider sx={{ my: 2 }}>
                    <Typography variant="subtitle1" color="text.secondary">
                      Deposit Details
                    </Typography>
                  </Divider>
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Deposit Date"
                    name="deposit_date"
                    value={depositForm.deposit_date}
                    onChange={handleFormChange}
                    required
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth error={!!formErrors.periods}>
                    <InputLabel>Period (Months)</InputLabel>
                    <Select
                      name="periods"
                      value={depositForm.periods}
                      onChange={handleFormChange}
                      required
                      label="Period (Months)"
                    >
                      <MenuItem value="3">3 Months</MenuItem>
                      <MenuItem value="6">6 Months</MenuItem>
                      <MenuItem value="12">12 Months (1 Year)</MenuItem>
                      <MenuItem value="24">24 Months (2 Years)</MenuItem>
                      <MenuItem value="36">36 Months (3 Years)</MenuItem>
                      <MenuItem value="60">60 Months (5 Years)</MenuItem>
                    </Select>
                    {formErrors.periods && <FormHelperText>{formErrors.periods}</FormHelperText>}
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="number"
                    label="Interest Rate (%)"
                    name="interestRate"
                    value={depositForm.interestRate}
                    onChange={handleFormChange}
                    required
                    inputProps={{ step: "0.1", min: "1", max: "20" }}
                    error={!!formErrors.interestRate}
                    helperText={formErrors.interestRate}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="date"
                    label="Maturity Date"
                    name="maturity_date"
                    value={depositForm.maturity_date}
                    InputProps={{ readOnly: true }}
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Maturity Amount (₹)"
                    name="maturity_amount"
                    value={depositForm.maturity_amount}
                    InputProps={{ readOnly: true }}
                  />
                </Grid>
              </Grid>

              <Alert severity="info" sx={{ mt: 3 }}>
                After creating the fixed deposit, you'll be able to download the receipt.
              </Alert>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAddModal(false)} disabled={isLoading}>
              Cancel
            </Button>
            <Button variant="contained" onClick={handleDepositSubmit} disabled={isLoading}>
              {isLoading ? "Creating..." : "Create Fixed Deposit"}
            </Button>
          </DialogActions>
        </StyledModal>

        <StyledModal open={showReceiptModal} onClose={() => setShowReceiptModal(false)}>
          <DialogTitle>
            Fixed Deposit Receipt
            <IconButton
              aria-label="close"
              onClick={() => setShowReceiptModal(false)}
              sx={{ position: "absolute", right: 8, top: 8 }}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          <DialogContent dividers>
            <Box id="fixed-deposit-receipt" className="fixed-deposit-receipt">
              <Box sx={{ textAlign: "center", mb: 3, pb: 2, borderBottom: "1px solid #000" }}>
                <Typography variant="h4">JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Typography>
                <Typography variant="h5">Rameswaram</Typography>
                <Typography>120-A, RAILWAY FEEDER ROAD, MARKET STREET, RAMESWARAM - 623526</Typography>
                <Paper
                  elevation={0}
                  sx={{ mt: 2, p: 1, border: "1px solid #000", maxWidth: 300, mx: "auto", fontWeight: "bold" }}
                >
                  FIXED DEPOSIT SCHEME
                </Paper>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      CIF Number:
                    </Typography>
                    <Typography>{selectedDeposit?.cifNumber ?? "N/A"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      Account No:
                    </Typography>
                    <Typography>{selectedDeposit?.accountNumber ?? "N/A"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      Customer Name:
                    </Typography>
                    <Typography>{selectedDeposit?.customerName ?? "N/A"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      Sr.No:
                    </Typography>
                    <Typography>{selectedDeposit?.srNo ?? "N/A"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      Nominee:
                    </Typography>
                    <Typography>{selectedDeposit?.nominee_name ?? "None"}</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: "flex", mb: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mr: 1 }}>
                      Deposit Date:
                    </Typography>
                    <Typography>
                      {selectedDeposit?.depositDate
                        ? new Date(selectedDeposit.depositDate).toLocaleDateString("en-GB")
                        : "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <TableContainer component={Paper} elevation={0} sx={{ mt: 3 }}>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Deposit Date</TableCell>
                          <TableCell>Deposit Amount</TableCell>
                          <TableCell>Period (Months)</TableCell>
                          <TableCell>Interest Rate (%)</TableCell>
                          <TableCell>Maturity Date</TableCell>
                          <TableCell>Maturity Amount</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow>
                          <TableCell>
                            {selectedDeposit?.depositDate
                              ? new Date(selectedDeposit.depositDate).toLocaleDateString("en-GB")
                              : "N/A"}
                          </TableCell>
                          <TableCell>{parseFloat(selectedDeposit?.amount ?? 0).toFixed(2)}</TableCell>
                          <TableCell>{selectedDeposit?.period ?? "N/A"}</TableCell>
                          <TableCell>{selectedDeposit?.interestRate ?? "N/A"}</TableCell>
                          <TableCell>
                            {selectedDeposit?.maturityDate
                              ? new Date(selectedDeposit.maturityDate).toLocaleDateString("en-GB")
                              : "N/A"}
                          </TableCell>
                          <TableCell>{parseFloat(selectedDeposit?.maturityAmount ?? 0).toFixed(2)}</TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                      Deposit Amount in Words:
                    </Typography>
                    <Typography>
                      {selectedDeposit?.amount ? convertToWords(parseFloat(selectedDeposit.amount)) : "N/A"}
                    </Typography>
                  </Box>
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: "bold", mb: 1 }}>
                      Maturity Amount in Words:
                    </Typography>
                    <Typography>
                      {selectedDeposit?.maturityAmount
                        ? convertToWords(parseFloat(selectedDeposit.maturityAmount))
                        : "N/A"}
                    </Typography>
                  </Box>
                </Grid>

                <Grid item xs={12}>
                  <Typography
                    variant="subtitle1"
                    sx={{ fontWeight: "bold", fontStyle: "italic", textAlign: "center", my: 2 }}
                  >
                    Not Transferable
                  </Typography>
                  <Typography sx={{ textAlign: "center", mb: 1 }}>
                    Thank you for your continued patronage with us.
                  </Typography>
                  <Typography sx={{ textAlign: "center", fontSize: "0.9rem", color: "text.secondary" }}>
                    * Maturity amount may vary subject to RBI regulations.
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ display: "flex", justifyContent: "space-between", mt: 4, pt: 4, borderTop: "1px dashed #ccc" }}>
                    <Box />
                    <Typography sx={{ textAlign: "center" }}>for JOTHILINGA FINANCIAL SOLUTIONS PVT.LTD</Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowReceiptModal(false)} disabled={isLoading}>
              Close
            </Button>
            <Button startIcon={<PrintIcon />} onClick={handlePrintReceipt} color="info" disabled={isLoading}>
              Print
            </Button>
            <Button
              startIcon={<DownloadIcon />}
              onClick={handleDownloadReceipt}
              variant="contained"
              disabled={isLoading}
            >
              Download PDF
            </Button>
          </DialogActions>
        </StyledModal>

        <Dialog open={showAlertModal} onClose={() => setShowAlertModal(false)}>
          <DialogContent>
            <Alert severity="info" sx={{ mb: 2 }}>
              {alertMessage}
            </Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setShowAlertModal(false)}>Close</Button>
          </DialogActions>
        </Dialog>
      </Box>
    </Container>
  );
};

export default React.memo(FixedDeposit);